/**
 * Displays data from Iceberg tables for a study.
 */

import Alert from "@cloudscape-design/components/alert"
import Box from "@cloudscape-design/components/box"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import Modal from "@cloudscape-design/components/modal"
import Select from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import { useEffect, useMemo, useState } from "react"
import {
  useTablesList,
  useTableData,
  usePrefetchRecordTimeline,
} from "@/hooks/queries/useIngestion"
import { useIngestionSpecsList } from "@/hooks/queries/useIngestionSpecs"
import RecordTimeline from "./RecordTimeline"

interface IngestedDataPanelProps {
  studyId: string
}

const IngestedDataPanel = ({ studyId }: IngestedDataPanelProps) => {
  const { data: tablesData, isPending: loading, error: tablesError } = useTablesList(studyId)
  const { data: specsData } = useIngestionSpecsList(studyId)
  const [selectedTable, setSelectedTable] = useState<string | null>(null)

  const tables = useMemo(() => tablesData?.tables ?? [], [tablesData])
  const ingestionSpecs = useMemo(() => specsData?.ingestionSpecs ?? [], [specsData])

  useEffect(() => {
    if (tables.length > 0 && !selectedTable) {
      setSelectedTable(tables[0])
    }
  }, [tables, selectedTable])

  const {
    data: tableDataResponse,
    isPending: loadingData,
    error: tableError,
  } = useTableData(studyId, selectedTable)
  const [selectedRecord, setSelectedRecord] = useState<{ pkValue: string } | null>(null)

  const records = useMemo(() => tableDataResponse?.records ?? [], [tableDataResponse])
  const error = tablesError || tableError

  const displayColumns = useMemo(() => {
    const cols = tableDataResponse?.columns?.length
      ? tableDataResponse.columns
      : records.length > 0
        ? Object.keys(records[0])
        : []
    return cols.filter((c) => !c.startsWith("_ctm_"))
  }, [tableDataResponse, records])

  const pkFields = useMemo((): string[] => {
    if (!selectedTable) return []
    const spec = ingestionSpecs.find((s) => {
      const specTableName = (s.spec as { table?: { name?: string } })?.table?.name?.toLowerCase()
      return (
        s.domain?.toLowerCase() === selectedTable.toLowerCase() ||
        specTableName === selectedTable.toLowerCase()
      )
    })
    const pkFieldsFromSpec = (spec?.spec as { table?: { primaryKeyFields?: string[] } })?.table
      ?.primaryKeyFields
    if (pkFieldsFromSpec?.length) return pkFieldsFromSpec.map((f: string) => f.toLowerCase())
    return displayColumns.length > 0 ? [displayColumns[0]] : []
  }, [selectedTable, ingestionSpecs, displayColumns])

  const prefetchRecordTimeline = usePrefetchRecordTimeline(studyId)
  useEffect(() => {
    if (!selectedTable || records.length === 0 || pkFields.length === 0) return

    records.slice(0, 10).forEach((record) => {
      const pkValue = pkFields.map((field) => record[field] ?? "").join(",")
      if (pkValue) prefetchRecordTimeline(selectedTable, pkValue)
    })
  }, [selectedTable, records, pkFields, prefetchRecordTimeline])

  const handleRowClick = (record: Record<string, string>) => {
    const pkValue = pkFields.map((field) => record[field] ?? "").join(",")
    if (pkValue) {
      setSelectedRecord({ pkValue })
    }
  }

  const columnDefinitions: TableProps.ColumnDefinition<Record<string, string>>[] =
    displayColumns.map((col) => ({
      id: col,
      header: col,
      cell: (record) => record[col],
    }))

  const tableSelector =
    tables.length > 0 ? (
      <Select
        selectedOption={selectedTable ? { label: selectedTable, value: selectedTable } : null}
        onChange={({ detail }) => setSelectedTable(detail.selectedOption.value ?? null)}
        options={tables.map((table) => ({ label: table, value: table }))}
        ariaLabel="Select table"
      />
    ) : undefined

  return (
    <Container
      header={
        <Header
          actions={tableSelector}
          description={records.length > 0 ? "Click a row to view record timeline" : undefined}
        >
          Ingested Data
        </Header>
      }
    >
      <SpaceBetween size="m">
        {error && <Alert type="error">{error.message}</Alert>}

        <div style={{ maxHeight: "400px", overflowY: "auto" }}>
          <Table
            variant="embedded"
            stickyHeader
            columnDefinitions={columnDefinitions}
            items={records}
            loading={loading || (!!selectedTable && loadingData && records.length === 0)}
            loadingText="Loading data..."
            trackBy={(record) => displayColumns.map((col) => record[col]).join("|")}
            onRowClick={({ detail }) => handleRowClick(detail.item)}
            empty={
              <Box textAlign="center" color="inherit">
                {tables.length === 0
                  ? "No ingested data tables found yet."
                  : "No records to display."}
              </Box>
            }
          />
        </div>

        {records.length > 0 && (
          <Box color="text-body-secondary" fontSize="body-s">
            Showing first {records.length} records (preview)
          </Box>
        )}
      </SpaceBetween>

      {selectedRecord && selectedTable && (
        <Modal
          visible
          onDismiss={() => setSelectedRecord(null)}
          header={`Record History: ${selectedRecord.pkValue}`}
          size="large"
        >
          <RecordTimeline
            studyId={studyId}
            tableName={selectedTable}
            primaryKeyValue={selectedRecord.pkValue}
          />
        </Modal>
      )}
    </Container>
  )
}

export default IngestedDataPanel
